import { Checkbox, DatePicker, Form, Input, Radio, Select } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { BaseOptionType, DefaultOptionType } from 'antd/es/select';
import { t } from 'i18next';
import { concat } from 'lodash';
import { useEffect, useState } from 'react';
import FormComponent from 'src/app/components/FormComponent/FormComponent';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import { getTypeOfField } from 'src/app/forms/AddNotificationForm/add-notification-form.config';
import { IFormProps } from 'src/app/forms/form.config';
import { getEnumSelectOptions } from 'src/app/helpers/enum.helper';
import { convertToSelectOptionsWithId } from 'src/app/helpers/form.helper';
import { useCreateGeneralReportMutation } from 'src/app/redux/generalReports/general-reports.slice';
import { useAppSelector } from 'src/app/redux/hook';
import { useGetSensorsIdentifiedQuery } from 'src/app/redux/sensors/sensorIdentifiedSlice';
import { useGetUsersQuery } from 'src/app/redux/user/userSlice';
import { MEDIA_TYPE_WHITELIST, SENSOR_CATEGORY_TYPE_WHITELIST, SENSOR_TYPE_WHITELIST } from 'src/configs/system.config';
import { GeneralReportType } from 'src/enums/general-report.enum';
import { SensorCategoryType, SensorType } from 'src/enums/nginr-type.enum';
import { EmailPeriod } from 'src/enums/sc3-type.enum';
import { MediaType } from 'src/enums/surfer.enums';
import { RecipientsArray } from 'src/types/rule.type';
import { User } from 'src/types/user.type';

interface IGeneralReportFormProps extends IFormProps {
  type: GeneralReportType;
}
const GeneralReportForm: React.FC<IGeneralReportFormProps> = ({ form, type }) => {
  const { RangePicker } = DatePicker;

  const sensors = Form.useWatch('sensors', form);

  const { data: sensorList } = useGetSensorsIdentifiedQuery({ ids: sensors }, { skip: !sensors?.length });

  const [createGeneralReport] = useCreateGeneralReportMutation();

  useEffect(() => {
    const date = form.getFieldValue('date');
    if (date && !!date[0] && !!date[1]) {
      form.setFieldValue('period', 'instant');
    }
  }, [form]);

  const onFinish = (values: any) => {
    const recipients: RecipientsArray[] = [];

    (['to', 'bcc', 'cc'] as const).forEach((field) => {
      if (values[field]) {
        const typeRecipient = getTypeOfField(field);
        if (typeRecipient === -1) return;
        recipients.push(
          ...values[field].map((opt: BaseOptionType) => ({
            recipientId: opt.value,
            type,
          })),
        );
      }
    });
    createGeneralReport({
      ...values,
      type,
      startTime: values.date ? values.date[0] : undefined,
      endTime: values.date ? values.date[1] : undefined,
      filter: 'all',
      period: type === GeneralReportType.Student ? 'instant' : values.period,
      recipients,
    });
  };

  const [selectedMails, setSelectedMails] = useState<string[]>([]);

  const { customerId } = useAppSelector((state) => state.user);
  const { data: userList } = useGetUsersQuery({ customerIds: customerId ?? undefined });

  const defaultEmailPeriods: DefaultOptionType[] = getEnumSelectOptions(EmailPeriod).filter(
    (opt) => opt.value !== EmailPeriod.Never,
  );
  const periodHiddenValue = EmailPeriod.Never;
  const periodLabelByValue = new Map<EmailPeriod, string>([
    ...defaultEmailPeriods.map((o) => [Number(o.value), o.label as string] as const),
    [periodHiddenValue, EmailPeriod[periodHiddenValue]],
  ]);

  const onValuesChange = (values: any, allValues: any) => {
    if (values.period) {
      form.setFieldValue(
        'emailPeriod',
        defaultEmailPeriods.find((o) => o.label?.toString().toLocaleLowerCase() === values.period)?.value,
      );
    }

    if (values.period && values.emailPeriod) {
      form.setFieldValue('emailPeriod', values.emailPeriod);
    }

    const mails = concat(
      allValues.to?.map((opt: DefaultOptionType) => opt.value) || [],
      allValues.cc?.map((opt: DefaultOptionType) => opt.value) || [],
      allValues.bcc?.map((opt: DefaultOptionType) => opt.value) || [],
    );
    setSelectedMails(mails);
  };

  const userListOptions = (isSelectedFilter?: boolean, callback?: (user: User) => boolean) => {
    if (!userList) return [];
    const result = callback ? userList.rows.filter(callback) : userList.rows;

    return result
      .filter((usr) => (isSelectedFilter ? !selectedMails.includes(usr.id) : usr))
      .map((usr) => ({ label: usr.fullname, value: usr.id }));
  };

  return (
    <FormComponent options={{ form, onFinish, onValuesChange }}>
      <Form.Item label="Report name" name="name" rules={[{ required: true }]}>
        <Input variant="borderless" />
      </Form.Item>

      {type === GeneralReportType.NSearch ? (
        <>
          <Form.Item label="Data Source Category" name="sensorCategoryTypes">
            <Select
              variant="borderless"
              mode="multiple"
              disabled
              options={getEnumSelectOptions(SensorCategoryType, SENSOR_CATEGORY_TYPE_WHITELIST())}
            />
          </Form.Item>
          <Form.Item label="Context" name="context">
            <TextArea
              variant="borderless"
              placeholder="Provide a brief explanation about context of report."
              style={{ resize: 'none' }}
            />
          </Form.Item>
          <Form.Item label="Data Source Type" name="sensorTypes">
            <Select
              variant="borderless"
              mode="multiple"
              disabled
              options={getEnumSelectOptions(SensorType, SENSOR_TYPE_WHITELIST())}
            />
          </Form.Item>
          <Form.Item label="Data Source" name="sensors">
            <Select
              variant="borderless"
              mode="multiple"
              options={convertToSelectOptionsWithId(sensorList?.rows || [])}
              disabled
            />
          </Form.Item>
          <Form.Item label="Interests" name="tags">
            <Select variant="borderless" mode="multiple" disabled />
          </Form.Item>
          <Form.Item label="Media Type" name="mediaTypes">
            <Select
              variant="borderless"
              mode="multiple"
              disabled
              options={getEnumSelectOptions(MediaType, MEDIA_TYPE_WHITELIST())}
            />
          </Form.Item>
          <Form.Item label="Date" name="date">
            <RangePicker
              showTime
              allowEmpty={[true, true]}
              // value={[dayjs(form.getFieldValue('startTime')), dayjs(form.getFieldValue('endTime'))]}
              variant="borderless"
              style={{ width: '100%' }}
              disabled
            />
          </Form.Item>
          <Form.Item name="categories" noStyle>
            <Input type="hidden" />
          </Form.Item>

          {/* <Form.Item label="Filter" name="filter" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="table">Table Data</Radio>
              <Radio value="all">All</Radio>
            </Radio.Group>
          </Form.Item> */}
          <Form.Item label="Period" name="period" rules={[{ required: true }]}>
            <Radio.Group
              defaultValue={!!form.getFieldValue('date')[0] && !!form.getFieldValue('date')[1] ? 'instantly' : null}
            >
              <Radio value="instantly">Instant</Radio>
              <Radio value="daily" disabled={!!form.getFieldValue('date')[0] && !!form.getFieldValue('date')[1]}>
                Daily
              </Radio>
              <Radio value="weekly" disabled={!!form.getFieldValue('date')[0] && !!form.getFieldValue('date')[1]}>
                Weekly
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('forms.ruleForm.emailNotification')}
            name="emailNotification"
            wrapperCol={{ flex: '20px', style: { borderLeft: 'none' } }}
            valuePropName="checked"
            labelCol={{ flex: 1, style: { order: 2, paddingLeft: '0' } }}
            style={{ paddingLeft: '1rem' }}
          >
            <Checkbox
            // disabled={!!recordId && editData?.type !== RuleEnum.SCENARIO}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => {
              return prevValues.emailNotification !== currentValues.emailNotification;
            }}
          >
            {({ getFieldValue }) =>
              getFieldValue('emailNotification') && (
                <>
                  <Form.Item label={t('forms.ruleForm.period')} rules={[{ required: true }]} name="emailPeriod">
                    <SearchableSelect
                      variant="borderless"
                      options={defaultEmailPeriods}
                      labelRender={({ value }) => periodLabelByValue.get(Number(value)) ?? value}
                      // disabled={editData?.type === RuleEnum.SCENARIO || isDefined(selectedSemanticValidator)}
                    />
                  </Form.Item>

                  {/* <Form.Item label={t('forms.ruleForm.firstTriggerDate')} name="firstTriggerDate">
                    <DatePicker
                      showTime
                      variant="borderless"
                      placeholder="Select date"
                      format="YYYY-MM-DD HH:mm"
                      disabled={
                        getFieldValue('emailPeriod') === EmailPeriod.Never ||
                        getFieldValue('emailPeriod') === EmailPeriod.Instantly
                      }
                      disabledDate={disabledDate}
                    />
                  </Form.Item> */}

                  <Form.Item label={t('forms.ruleForm.to')} rules={[{ required: true }]} name="to">
                    <SearchableSelect
                      labelInValue
                      variant="borderless"
                      // options={userListOptions(true, true)}
                      options={userListOptions(true, (usr) => !!usr.email)}
                      mode="multiple"
                    />
                  </Form.Item>
                  <Form.Item label={t('forms.ruleForm.cc')} name="cc">
                    <SearchableSelect
                      labelInValue
                      variant="borderless"
                      // options={userListOptions(true, true)}
                      options={userListOptions(true, (usr) => !!usr.email)}
                      mode="multiple"
                      // disabled={!!recordId}
                    />
                  </Form.Item>
                  <Form.Item label={t('forms.ruleForm.bcc')} name="bcc">
                    <SearchableSelect
                      labelInValue
                      variant="borderless"
                      // options={userListOptions(true, true)}
                      options={userListOptions(true, (usr) => !!usr.email)}
                      mode="multiple"
                    />
                  </Form.Item>
                </>
              )
            }
          </Form.Item>
        </>
      ) : null}
    </FormComponent>
  );
};

export default GeneralReportForm;
